import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";

// Session id stable across a single browser tab; not stored persistently so we
// only count unique-per-session visitors, not per-person. Zero cookies.
function getVisitorSessionId() {
  if (typeof window === "undefined") return "";
  try {
    let sid = window.sessionStorage.getItem("wag_vsid");
    if (!sid) {
      sid = `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      window.sessionStorage.setItem("wag_vsid", sid);
    }
    return sid;
  } catch { return ""; }
}

// GA4 SPA page-view tracker. Fires a `page_view` event on every client-side
// route change so React Router navigations aren't invisible to Google Analytics.
// Also pings our own /api/visitor/ping so the admin dashboard can show unique
// visitor counts for 30-min / 24-h / 7-d / 28-d windows.
export default function GATracker({ measurementId = "G-FLPKXFFWZS" }) {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const page_path = pathname + search;

    // ── GA4 ────────────────────────────────────────────────────────────────
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path,
        page_location: window.location.href,
        page_title: document.title,
        send_to: measurementId,
      });
    }

    // ── Internal visitor ping — fire-and-forget, never blocks render ───────
    // Skip admin routes; admins hitting their own panel shouldn't inflate
    // visitor counts.
    if (!pathname.startsWith("/admin")) {
      const sid = getVisitorSessionId();
      if (sid) {
        const referrer = document.referrer || undefined;
        // Parse UTM params so we can attribute paid / organic sources.
        const params = new URLSearchParams(search || window.location.search);
        const utm = {};
        ["utm_source","utm_medium","utm_campaign","utm_term","utm_content"].forEach((k) => {
          const v = params.get(k);
          if (v) {
            utm[k] = v;
            // Persist first-touch attribution for downstream forms.
            try {
              if (!window.sessionStorage.getItem(`ft_${k}`)) {
                window.sessionStorage.setItem(`ft_${k}`, v);
              }
            } catch {}
          }
        });
        Promise.resolve().then(() => {
          api.post("/visitor/ping", { session_id: sid, path: page_path, referrer, ...utm })
            .catch(() => { /* silent — analytics must never break UX */ });
        });
      }
    }
  }, [pathname, search, measurementId]);

  return null;
}
